import { useEffect } from 'react'
import { Grid } from '@mui/material'
import { useField, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import {
  DatePickerField,
  InputField,
  TimePickerField,
  AutoCompleteField,
  TextareaField
} from 'src/components/shared/ui/form/fields'
import FieldsetGroup from 'src/components/shared/ui/form/fields/FieldsetGroup/FieldsetGroup'
import useNumberValidations from 'src/components/shared/ui/form/fields/hooks/useNumberValidations'
import DatosSwitches from 'src/components/views/EscritosLexnet/components/IniciadorAsunto/components/Datos/DatosSwitches'
import MonitorioAviso from 'src/components/views/EscritosLexnet/components/IniciadorAsunto/components/Datos/MonitorioAviso'
import MateriaOrDelitoAutocomplete from 'src/components/views/EscritosLexnet/components/shared/components/iniciadores/MateriaOrDelitoAutocomplete'
import { EscritosFormValues } from 'src/components/views/EscritosLexnet/components/shared/types/escritosFormTypes'
import { Orden as OrdenMaster, TipoCuantiaOpciones } from 'src/enums/EscritosLegales'
import { FormField } from 'src/enums/Form'
import { Orden, TipoCuantia } from 'src/interfaces/Catalog'

function Datos(): JSX.Element {
  const { t } = useTranslation()
  const { setFieldValue, values } = useFormikContext<EscritosFormValues>()
  const { allowFloatNumber } = useNumberValidations()
  const [orden] = useField<Orden>(
    `${FormField.ORGANO_DESTINO}.${FormField.ORGANO_DESTINO_ORDEN_JUDICIAL}`
  )
  const datosTipoCuantia: TipoCuantia | null = values[FormField.DATOS_TIPO_CUANTIA]
  const datosNumeroExpedienteOrigen = values[FormField.DATOS_NUMERO_EXPEDIENTE_ORIGEN]
  const datosYearExpedienteOrigen = values[FormField.DATOS_YEAR_EXPEDIENTE_ORIGEN]

  const { allowInteger } = useNumberValidations()

  function showCuantiaFields(): JSX.Element | null {
    const options = [OrdenMaster.CIVIL, OrdenMaster.CONTENCIOSO, OrdenMaster.SOCIAL]

    if (orden.value && options.includes(orden.value.orden)) {
      return (
        <>
          <Grid item xs={6} sm={4}>
            <AutoCompleteField
              id={FormField.DATOS_TIPO_CUANTIA}
              items={[
                { codigo: 'N', descripcion: TipoCuantiaOpciones.NO_PROCEDE },
                { codigo: 'I', descripcion: TipoCuantiaOpciones.INDETERMINADA },
                { codigo: 'D', descripcion: TipoCuantiaOpciones.DETERMINADA }
              ]}
              label={t('form.field.datos_tipo_cuantia')}
              itemName="descripcion"
              itemKey="codigo"
              required
            />
          </Grid>
          {datosTipoCuantia && datosTipoCuantia.codigo === 'D' && (
            <Grid item xs={6} sm={4}>
              <InputField
                id={FormField.DATOS_CUANTIA_PRINCIPAL}
                label={t('form.field.datos_cuantia_principal')}
                clearable
                currencyType
                required
                onChange={allowFloatNumber}
                maxLength={16}
              />
            </Grid>
          )}
        </>
      )
    }
    return null
  }

  function showOtherPenalFields(): JSX.Element | null {
    if (orden.value && orden.value.orden === OrdenMaster.PENAL) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <InputField
              id={FormField.DATOS_LOCALIDAD}
              label={t('form.field.localidad')}
              clearable
              maxLength={200}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <DatePickerField id={FormField.DATOS_FECHA} label={t('form.field.fecha')} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TimePickerField id={FormField.DATOS_HORA} label={t('form.field.hora')} />
          </Grid>
        </Grid>
      )
    }
    return null
  }

  function showOtherContenciosoFields(): JSX.Element | null {
    if (orden.value && orden.value.orden === OrdenMaster.CONTENCIOSO) {
      return (
        <>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <DatePickerField
                id={FormField.DATOS_FECHA_ACUERDO}
                label={t('form.field.datos_fecha_acuerdo')}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <InputField
                id={FormField.DATOS_OBJETO_RECURSO}
                label={t('form.field.datos_objeto_recurso')}
                clearable
                maxLength={70}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <InputField
                id={FormField.DATOS_ORGANISMO}
                label={t('form.field.datos_organismo')}
                clearable
                required
                maxLength={200}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <InputField
                id={FormField.DATOS_NUMERO_EXPEDIENTE_ORIGEN}
                label={t('form.field.datos_numero_expediente_origen')}
                maxLength={20}
                onChange={allowInteger}
                clearable
                required={Boolean(datosNumeroExpedienteOrigen || datosYearExpedienteOrigen)}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <InputField
                id={FormField.DATOS_YEAR_EXPEDIENTE_ORIGEN}
                label={t('form.field.datos_year_expediente_origen')}
                maxLength={4}
                onChange={allowInteger}
                clearable
                required={Boolean(datosNumeroExpedienteOrigen || datosYearExpedienteOrigen)}
              />
            </Grid>
          </Grid>
        </>
      )
    }
    return null
  }

  useEffect(() => {
    if (datosTipoCuantia?.descripcion !== TipoCuantiaOpciones.DETERMINADA) {
      setFieldValue(FormField.DATOS_CUANTIA_PRINCIPAL, '')
    }
  }, [datosTipoCuantia, setFieldValue])

  return (
    <FieldsetGroup legend={t('form.legend.datos')}>
      <Grid container spacing={2}>
        <MateriaOrDelitoAutocomplete />

        {showCuantiaFields()}
      </Grid>
      <MonitorioAviso />
      {showOtherPenalFields()}
      {showOtherContenciosoFields()}
      <TextareaField
        id={FormField.DATOS_OBSERVACIONES}
        label={t('form.field.observaciones')}
        maxLength={1000}
        disabled={!orden.value}
      />
      <DatosSwitches />
    </FieldsetGroup>
  )
}

export default Datos
